import { useContext, useEffect, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useStyles } from "./CorporateDashboard.styles";
import { MenuItem, Select } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CorporateDashboardContext } from "contexts/CorporateDashboardContext/CorporateDashboardContext";
import CorporateReports from "./CorporateReports/CorporateReports.view";
import DateRange from "components/DateRange/DateRange.view";
import { useNavigate } from "react-router-dom";
import { CORPORATE_BACKOFFICE_PATHS, DisplayInOptionsEnum } from "constants/constants";
import { useAllowCorporateDashboard } from "hooks/useAllowCorporateDashboard";
import PrintIcon from "@mui/icons-material/Print";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import CorporateDashboardLayout from "./CorporateDashboardLayout/CorporateDashboardLayout.view";

const CorporateDashboard = () => {
    const classes = useStyles();
    const { handleSetFilters, filters, resetSelections } = useContext(CorporateDashboardContext);
    const [dateRangeFilter, setDateRangeFilter] = useState({ startDate: filters.startDate, endDate: filters.endDate });
    const { isAllowedToUseCorporateDashboard, isCorporationsListLoaded } = useAllowCorporateDashboard();
    const [isCheckingAccessToCorporateDashboard, setIsCheckingAccessToCorporateDashboard] = useState<boolean>(true);
    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        if (isCheckingAccessToCorporateDashboard && isCorporationsListLoaded) {
            if (!isAllowedToUseCorporateDashboard) {
                navigate(CORPORATE_BACKOFFICE_PATHS.CORPORATE_DASHBOARD);
            } else {
                setIsCheckingAccessToCorporateDashboard(false);
            }
        }
    }, [isCheckingAccessToCorporateDashboard, navigate, isCorporationsListLoaded, isAllowedToUseCorporateDashboard]);

    if (isCheckingAccessToCorporateDashboard) {
        return (
            <CorporateDashboardLayout>
                <div className={classes.mainWrapper}>{t("corporate_dashboard.checking_access")}</div>
            </CorporateDashboardLayout>
        );
    }
    return (
        <CorporateDashboardLayout>
            <div className={classes.mainWrapper}>
                <div className={classes.header}>
                    <div className={classes.headerSide}>
                        <DateRange
                            className={classes.dateRange}
                            startDate={dateRangeFilter.startDate}
                            endDate={dateRangeFilter.endDate}
                            onChange={(update) => {
                                const [start, end] = update as [Date, Date];
                                setDateRangeFilter({ startDate: start, endDate: end });
                                if (start && end) {
                                    handleSetFilters({ ...filters, startDate: start, endDate: end });
                                    resetSelections();
                                }
                            }}
                            label={t("corporate_dashboard.filters.date_range.title")}
                            placeholderText={t("corporate_dashboard.filters.date_range.placeholder")}
                        />
                        <div className={classes.reportSelectWrapper}>
                            <div className={classes.reportSelectLabel}>
                                {t("corporate_dashboard.filters.display_in.title")}
                            </div>
                            <Select
                                IconComponent={KeyboardArrowDownIcon}
                                className={classes.reportSelect}
                                value={filters.displayIn}
                                onChange={(event) => handleSetFilters({ ...filters, displayIn: event.target.value })}
                            >
                                <MenuItem value={DisplayInOptionsEnum.PERCENT}>
                                    {t("corporate_dashboard.filters.display_in.percent")}
                                </MenuItem>
                                <MenuItem value={DisplayInOptionsEnum.MINUTES}>
                                    {t("corporate_dashboard.filters.display_in.minutes")}
                                </MenuItem>
                                <MenuItem value={DisplayInOptionsEnum.OCCURRENCES}>
                                    {t("corporate_dashboard.filters.display_in.occurrences")}
                                </MenuItem>
                            </Select>
                        </div>
                    </div>
                    {/* TODO: enable print button when functionality is ready */}
                    <div className={`${classes.headerSide} ${classes.headerActions}`} style={{ display: "none" }}>
                        <div className={classes.headerActionWrapper}>
                            <ToggleButtonGroup
                                color="info"
                                className={classes.headerFilterButton}
                                value={""}
                                exclusive
                                onChange={() => null}
                            >
                                <ToggleButton disabled value="print">
                                    <PrintIcon />
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </div>
                    </div>
                </div>
                <CorporateReports />
            </div>
        </CorporateDashboardLayout>
    );
};

export default CorporateDashboard;
